/**
 * Org-studio customer activity: merge buyer opens, inquiries, submitted selections,
 * share-link admin rows, and security events into one newest-first timeline.
 */

export type CustomerActivityKind =
  | "access"
  | "inquiry"
  | "extension"
  | "selection"
  | "share"
  | "security";

export type CustomerActivityFilter = "all" | "buyer" | CustomerActivityKind;

export type CustomerActivityHrefTab = "inquiries" | "products";

export type CustomerActivityItem = {
  id: string;
  kind: CustomerActivityKind;
  atMs: number;
  description: string;
  clientName?: string;
  clientPhone?: string;
  inquiryId?: string;
  submissionId?: string;
  submissionKind?: "cart" | "wishlist";
  productUniqueId?: string;
  hrefTab?: CustomerActivityHrefTab;
};

const EXTENSION_SOURCE = "CATALOGUE_EXTENSION_REQUEST";

export function parseActivityDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "object" && value !== null && "$date" in value) {
    return parseActivityDate((value as { $date: unknown }).$date);
  }
  return null;
}

export function activityRangeStart(timeRange: string): Date {
  const days = timeRange === "7d" ? 7 : timeRange === "90d" ? 90 : 30;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

/** Undated rows are excluded from a clipped range (honest empty vs “maybe”). */
export function inActivityRange(value: unknown, start: Date): boolean {
  const d = parseActivityDate(value);
  if (!d) return false;
  return d.getTime() >= start.getTime();
}

export function mongoDocId(doc: any): string {
  const raw = doc?.id ?? doc?._id;
  if (raw && typeof raw === "object" && "$oid" in raw) {
    return String((raw as { $oid: string }).$oid);
  }
  return String(raw ?? "").trim();
}

export function isInviteeView(row: any): boolean {
  if (!row || row.internalUser === true) return false;
  const kind = String(row.viewerKind || "").toUpperCase();
  if (kind === "STUDIO") return false;
  if (kind === "INVITEE") return true;
  return String(row.userId || "").startsWith("invitee:");
}

export function inquiryOccurredAt(inquiry: any): unknown {
  return inquiry?.inquiryDate || inquiry?.createdAt || inquiry?.submittedAt || inquiry?.lastUpdated;
}

export function digitsOnlyPhone(raw: unknown): string {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length >= 12 && d.startsWith("91")) return d.slice(-10);
  if (d.length > 10) return d.slice(-10);
  return d;
}

/** wa.me with country code; 10-digit Indian numbers get 91. */
export function catalogueWhatsAppHref(phone: unknown, text?: string): string {
  let d = digitsOnlyPhone(phone);
  if (!d) return "";
  if (d.length === 10) d = `91${d}`;
  const q = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${d}${q}`;
}

function firstProductUniqueId(lines: any[]): string | undefined {
  for (const line of lines || []) {
    const id = String(line?.productUniqueId || "").trim();
    if (id) return id;
  }
  return undefined;
}

export function buildCustomerActivityFeed(input: {
  views?: any[];
  links?: any[];
  inquiries?: any[];
  events?: any[];
  wishlists?: any[];
  carts?: any[];
  start: Date;
}): CustomerActivityItem[] {
  const items: CustomerActivityItem[] = [];
  const start = input.start;

  for (const view of input.views || []) {
    if (!isInviteeView(view)) continue;
    const at = view?.lastViewDate || view?.lastViewedAt || view?.firstViewDate;
    if (!inActivityRange(at, start)) continue;
    const d = parseActivityDate(at);
    if (!d) continue;
    const name = String(view?.recipientName || view?.userName || "").trim();
    const phone = digitsOnlyPhone(view?.recipientPhone);
    const who = name || (phone ? `Buyer ···${phone.slice(-4)}` : "Buyer");
    items.push({
      id: `access-${mongoDocId(view) || phone || d.getTime()}`,
      kind: "access",
      atMs: d.getTime(),
      description: `${who} opened this catalogue`,
      clientName: name || undefined,
      clientPhone: phone || undefined,
    });
  }

  for (const link of input.links || []) {
    if (!inActivityRange(link?.createdAt, start)) continue;
    const d = parseActivityDate(link.createdAt);
    if (!d) continue;
    const name = String(link?.recipientName || "").trim() || "recipient";
    items.push({
      id: `share-${mongoDocId(link) || d.getTime()}`,
      kind: "share",
      atMs: d.getTime(),
      description: `Shared with ${name}`,
      clientName: name === "recipient" ? undefined : name,
      clientPhone: digitsOnlyPhone(link?.recipientPhone) || undefined,
    });
  }

  for (const inquiry of input.inquiries || []) {
    const at = inquiryOccurredAt(inquiry);
    if (!inActivityRange(at, start)) continue;
    const d = parseActivityDate(at);
    if (!d) continue;
    const name = String(inquiry?.clientName || inquiry?.customerName || "").trim() || "Buyer";
    const phone = digitsOnlyPhone(inquiry?.clientContact || inquiry?.phone);
    const source = String(inquiry?.source || "");
    const isExtension = source === EXTENSION_SOURCE;
    const id = mongoDocId(inquiry);
    const productUniqueId = String(
      inquiry?.productItems?.[0]?.productUniqueId || inquiry?.productIds?.[0] || ""
    ).trim();
    items.push({
      id: `${isExtension ? "extension" : "inquiry"}-${id || d.getTime()}`,
      kind: isExtension ? "extension" : "inquiry",
      atMs: d.getTime(),
      description: isExtension
        ? `${name} requested more time on the share link`
        : `${name} sent an inquiry`,
      clientName: name,
      clientPhone: phone || undefined,
      inquiryId: id || undefined,
      productUniqueId: productUniqueId || undefined,
      hrefTab: "inquiries",
    });
  }

  const pushSelection = (doc: any, kind: "cart" | "wishlist") => {
    const at = doc?.submissionDate || doc?.lastUpdated;
    if (!inActivityRange(at, start)) return;
    const d = parseActivityDate(at);
    if (!d) return;
    const lines = kind === "cart" ? doc?.cartItems : doc?.wishlistItems;
    const list = Array.isArray(lines) ? lines : [];
    const n = list.length;
    const name = String(doc?.clientName || "").trim() || "Buyer";
    const phone = digitsOnlyPhone(doc?.clientContact);
    const id = mongoDocId(doc);
    const label = kind === "cart" ? "cart" : "shortlist";
    items.push({
      id: `selection-${kind}-${id || d.getTime()}`,
      kind: "selection",
      atMs: d.getTime(),
      description: `${name} submitted ${n} ${label} item${n === 1 ? "" : "s"}`,
      clientName: name,
      clientPhone: phone || undefined,
      submissionId: id || undefined,
      submissionKind: kind,
      productUniqueId: firstProductUniqueId(list),
      hrefTab: "inquiries",
    });
  };

  for (const w of input.wishlists || []) pushSelection(w, "wishlist");
  for (const c of input.carts || []) pushSelection(c, "cart");

  for (const event of input.events || []) {
    if (!inActivityRange(event?.createdAt, start)) continue;
    const d = parseActivityDate(event.createdAt);
    if (!d) continue;
    items.push({
      id: `security-${mongoDocId(event) || d.getTime()}`,
      kind: "security",
      atMs: d.getTime(),
      description: String(event?.eventType || "Security event").replace(/_/g, " "),
      clientName: event?.actorRole || event?.actorUserId || undefined,
    });
  }

  items.sort((a, b) => b.atMs - a.atMs);
  return items;
}

export function isBuyerActivityKind(kind: CustomerActivityKind): boolean {
  return kind === "access" || kind === "inquiry" || kind === "extension" || kind === "selection";
}

export function filterCustomerActivity(
  items: CustomerActivityItem[],
  filter: CustomerActivityFilter
): CustomerActivityItem[] {
  if (filter === "all") return items;
  if (filter === "buyer") return items.filter((row) => isBuyerActivityKind(row.kind));
  return items.filter((row) => row.kind === filter);
}

export function uniqueBuyersInRange(views: any[], start: Date): number {
  const ids = new Set<string>();
  for (const view of views || []) {
    if (!isInviteeView(view)) continue;
    const at = view?.lastViewDate || view?.lastViewedAt || view?.firstViewDate;
    if (!inActivityRange(at, start)) continue;
    const key = String(view?.userId || view?.recipientPhone || "").trim();
    if (key) ids.add(key);
  }
  return ids.size;
}

export function submittedSelectionCount(wishlists: any[], carts: any[], start: Date): number {
  let n = 0;
  for (const w of wishlists || []) {
    if (inActivityRange(w?.submissionDate || w?.lastUpdated, start)) n += 1;
  }
  for (const c of carts || []) {
    if (inActivityRange(c?.submissionDate || c?.lastUpdated, start)) n += 1;
  }
  return n;
}

export function expiredOrDisabledLinkCount(links: any[], start: Date, nowMs = Date.now()): number {
  return (links || []).filter((l: any) => {
    if (l?.disabled) return inActivityRange(l?.disabledAt || l?.expiresAt || l?.createdAt, start);
    const exp = parseActivityDate(l?.expiresAt);
    return Boolean(exp && exp.getTime() < nowMs && inActivityRange(l?.expiresAt, start));
  }).length;
}
